import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { aboutPhotos } from "../lib/aboutPhotos";
import SkylineDivider from "../components/site/SkylineDivider";
import ServiceMarker from "../components/site/ServiceMarker";
import { CornerBracket, HexMark } from "../components/site/LogoMotif";

const STATS = [
  { n: "2,500+", l: "Companies advised", marker: "map-nodes" },
  { n: "24", l: "Countries represented", marker: "assessment-scope" },
  { n: "150+", l: "Successful entries supported into the Kingdom", marker: "build-signature" },
  { n: "10", l: "Embassies and trade agencies advised", marker: "arm-signature" },
];

const BELIEFS = [
  {
    t: "Clarity should come before cost",
    b: "Companies should not have to pay just to understand whether the Kingdom makes sense. HuddleUp believes the first layer of truth should be shared early, clearly, and honestly, so companies can make informed decisions before committing capital, time, or internal resources.",
  },
  {
    t: "Setup is not the strategy",
    b: "A company can be registered and still have no market. HuddleUp keeps setup, hiring, providers, and infrastructure connected to the commercial reason for entering the Kingdom.",
  },
  {
    t: "The right work should happen in the right order",
    b: "Saudi entry becomes expensive when companies do too much too early or too little too late. HuddleUp helps companies prove demand, stay represented, and build only what the market justifies.",
  },
  {
    t: "Growth should support the Kingdom",
    b: "Entering Saudi should not only be about foreign companies winning new revenue. HuddleUp supports market entry that contributes to the Kingdom’s growth, creates meaningful commercial activity, and helps develop Saudi talent, capability, and long term participation.",
  },
];

function PhotoBlock({ src, className, priority }) {
  return (
    <div
      className={`relative overflow-hidden group bg-kingdom ${className}`}
      data-testid={src ? "about-photo" : "about-photo-placeholder"}
    >
      {src ? (
        <>
          <img
            src={src}
            alt=""
            loading={priority ? "eager" : "lazy"}
            className="w-full h-full object-cover transition-all duration-1000 ease-out group-hover:scale-[1.03]"
          />
          <div
            className="absolute inset-0 pointer-events-none transition-opacity duration-1000 ease-out group-hover:opacity-40"
            style={{
              background:
                "linear-gradient(180deg, rgba(15,42,36,0.20) 0%, rgba(15,42,36,0.05) 40%, rgba(10,10,10,0.45) 100%)",
              mixBlendMode: "multiply",
              opacity: 0.85,
            }}
          />
          <div className="absolute inset-x-0 bottom-0 h-[1px] bg-gold origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-700 ease-out" />
        </>
      ) : (
        <div className="w-full h-full bg-gradient-to-br from-kingdom to-obsidian relative">
          <div
            className="absolute inset-0 opacity-[0.06]"
            style={{
              backgroundImage:
                'url("data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 viewBox=%220 0 200 200%22 fill=%22none%22 stroke=%22%23C9A961%22 stroke-width=%220.5%22><polygon points=%22100,10 190,60 190,140 100,190 10,140 10,60%22/><polygon points=%22100,40 160,70 160,130 100,160 40,130 40,70%22/><line x1=%2210%22 y1=%2260%22 x2=%22190%22 y2=%22140%22/><line x1=%22190%22 y1=%2260%22 x2=%2210%22 y2=%22140%22/></svg>")',
              backgroundSize: "200px 200px",
            }}
          />
          <div className="absolute inset-0 flex items-end p-6">
            <span className="text-xs text-gold/60 tracking-[0.24em] uppercase">
              Editorial placeholder
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

export default function About() {
  const photos = [...aboutPhotos, null, null, null, null, null].slice(0, 5);

  return (
    <div>
      {/* HERO with subtle architectural depth */}
      <section
        data-testid="about-hero"
        className="bg-obsidian text-paper py-28 md:py-40 relative overflow-hidden"
      >
        <div className="skyline"></div>
        <div className="ambient-glow"></div>
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 relative z-10">
          <div className="mb-10"><HexMark size={22} /></div>
          <span className="eyebrow">About HuddleUp</span>
          <h1 className="font-display text-4xl sm:text-5xl md:text-[72px] leading-[1.02] mt-8 max-w-4xl">
            Built for companies that need <br />
            <span className="italic text-gold">more than advice.</span>
          </h1>

          <div className="mt-14 grid md:grid-cols-12 gap-12 max-w-6xl">
            <div className="md:col-span-8 space-y-6 text-[17px] text-warm/85 leading-relaxed">
              <p>
                HuddleUp was created for international companies that see
                opportunity in the Kingdom but need a sharper way to move from
                interest to evidence, representation, and local execution.
              </p>
              <p>
                Most market entry journeys become fragmented. A setup provider
                handles documents. A consultant gives advice. A recruiter talks
                about hiring. A contact makes an introduction. But the company
                is still left connecting the market, the opportunity, the
                providers, and the operating decisions from outside the
                Kingdom.
              </p>
              <p className="font-display text-xl italic text-gold">
                HuddleUp was built to sit in that gap.
              </p>
              <p>
                We act as the commercial arm for companies entering Saudi
                Arabia, helping them prove demand, stay represented, manage the
                right workstreams, and build only what the market justifies.
              </p>
            </div>
          </div>
        </div>
        <SkylineDivider variant="gold" opacity={0.2} height={160} className="skyline-bottom" />
      </section>

      {/* Editorial photo grid */}
      <section data-testid="about-photo-grid" className="bg-cream py-24 md:py-32 relative overflow-hidden">
        <div className="najdi-bg" style={{ opacity: 0.04 }}></div>
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 relative z-10">
          <div className="mb-14 grid md:grid-cols-12 gap-10 items-end">
            <div className="md:col-span-6">
              <span className="eyebrow eyebrow-dark">On The Ground</span>
              <h2 className="font-display text-3xl md:text-4xl leading-[1.05] mt-6 text-charcoal">
                Where HuddleUp shows up.
              </h2>
            </div>
            <p className="md:col-span-5 md:col-start-8 text-[15.5px] text-charcoal/65 leading-relaxed">
              Speaking at embassies, trade missions, and market entry programs
              across the Kingdom and beyond — the same rooms our clients need
              to walk into.
            </p>
          </div>

          <div className="grid grid-cols-6 grid-rows-2 gap-4 md:gap-6 h-[520px] md:h-[720px]">
            <PhotoBlock src={photos[0]} className="col-span-4 row-span-2" priority />
            <PhotoBlock src={photos[1]} className="col-span-2 row-span-1" />
            <PhotoBlock src={photos[2]} className="col-span-2 row-span-1" />
          </div>
          <div className="mt-4 md:mt-6 grid grid-cols-6 gap-4 md:gap-6 h-[220px] md:h-[320px]">
            <PhotoBlock src={photos[3]} className="col-span-3" />
            <PhotoBlock src={photos[4]} className="col-span-3" />
          </div>
        </div>
      </section>

      {/* Credibility — visually elevated stats with tower silhouette behind */}
      <section
        data-testid="about-credibility"
        className="bg-kingdom text-paper py-28 md:py-40 relative overflow-hidden"
      >
        <div className="arch-depth"></div>
        <div className="ambient-glow"></div>
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 relative z-10">
          <div className="grid md:grid-cols-12 gap-14">
            <div className="md:col-span-5">
              <span className="eyebrow">Credibility</span>
              <h2 className="font-display text-4xl md:text-5xl leading-[1.05] mt-6">
                Built by people who <br />
                <span className="italic text-gold">understand the work.</span>
              </h2>
            </div>
            <div className="md:col-span-6 md:col-start-7 space-y-6 text-[17px] text-warm/85 leading-relaxed">
              <p>
                HuddleUp brings together commercial entry, partnerships, setup
                coordination, provider management, and Saudi market execution
                into one operating model.
              </p>
              <p>
                Behind the model is direct experience advising 2,500+ companies
                across 24 countries, supporting 150+ successful entries into
                the Kingdom, and serving as an expert advisor to 10 embassies
                and trade agencies helping their companies expand into Saudi
                Arabia.
              </p>
            </div>
          </div>

          {/* Stats — pillar treatment with gold column + bespoke marker + corner brackets */}
          <div className="mt-24 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-y-14 gap-x-6">
            {STATS.map((s, i) => (
              <div
                key={s.n}
                data-testid={`about-stat-${i}`}
                className="group relative pl-8 pr-4 py-3 border-l border-gold/40 hover:border-gold transition-colors duration-700"
              >
                <div className="absolute -top-1 -right-1 opacity-40 group-hover:opacity-100 transition-opacity duration-500">
                  <CornerBracket position="tr" size={18} />
                </div>
                <div className="opacity-75 mb-8 group-hover:opacity-100 transition-opacity duration-500">
                  <ServiceMarker name={s.marker} />
                </div>
                <div className="font-display text-5xl md:text-6xl text-paper leading-none">
                  {s.n}
                </div>
                <div className="mt-5 text-[13px] text-warm/70 leading-snug max-w-[220px]">
                  {s.l}
                </div>
              </div>
            ))}
          </div>
        </div>
        <SkylineDivider variant="gold" opacity={0.16} height={140} className="skyline-bottom" />
      </section>

      {/* Beliefs — elevated card treatment */}
      <section data-testid="about-beliefs" className="bg-cream py-28 md:py-40 relative overflow-hidden">
        <div className="najdi-bg" style={{ opacity: 0.04 }}></div>
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 relative z-10">
          <div className="text-center max-w-3xl mx-auto">
            <span className="eyebrow eyebrow-dark">What we believe</span>
            <h2 className="font-display text-4xl md:text-6xl leading-[1.02] mt-6 text-charcoal">
              Four convictions <br />
              <span className="italic text-gold">that shape the work.</span>
            </h2>
          </div>

          <div className="mt-24 grid md:grid-cols-2 gap-8 md:gap-10">
            {BELIEFS.map((b, i) => (
              <div
                key={b.t}
                data-testid={`belief-${i}`}
                className="group relative bg-white/60 backdrop-blur-sm border border-charcoal/8 p-10 md:p-12 min-h-[280px] transition-all duration-700 hover:border-gold hover:bg-white hover:-translate-y-1"
              >
                <div className="flex items-baseline gap-5">
                  <span className="font-display text-2xl text-gold">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <div className="h-[1px] flex-1 bg-charcoal/15 group-hover:bg-gold/60 transition-colors duration-700"></div>
                </div>
                <h3 className="font-display text-2xl md:text-[26px] text-charcoal leading-snug mt-8">
                  {b.t}
                </h3>
                <p className="mt-6 text-[15px] text-charcoal/70 leading-relaxed">
                  {b.b}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section
        data-testid="about-cta"
        className="bg-obsidian text-paper py-28 md:py-40 relative overflow-hidden"
      >
        <div className="ambient-glow"></div>
        <div className="max-w-[1100px] mx-auto px-6 md:px-10 text-center relative z-10">
          <h2 className="font-display text-4xl md:text-6xl leading-[1.05]">
            Find your first move <br />
            <span className="italic text-gold">in the Kingdom.</span>
          </h2>
          <div className="mt-12 flex justify-center">
            <Link to="/diagnostic" data-testid="about-cta-button" className="btn-gold">
              Find Your First Move
              <ArrowRight size={16} />
            </Link>
          </div>
        </div>
        <SkylineDivider variant="gold" opacity={0.14} height={140} className="skyline-bottom" />
      </section>
    </div>
  );
}
